import { useQuery } from "@tanstack/react-query";
import { axios } from "./axios";
import { useAuth } from "../context/AuthContext";
import { env } from "../utils/env";
import { PagedResponse } from "../types/Common";

/**
 * Every screening watchlist match accumulated on a monitoring record, across
 * every activity and the initial screening it inherited.
 *
 * Thin by design: the backend serializer returns identity and decision only, no
 * matched name and no evidence. The page draws its cards from the activity
 * payloads instead (see useMonitoringActivityPayloads). This list is what tells
 * it the full set of matches exists at all, and carries the authoritative live
 * decision on each one.
 */
export interface MonitoringScreeningMatch {
  id: number;
  match_id: string | null;
  entity_unique_id: string | null;
  screening_record: number | null;
  status: string | null;
  status_display: string | null;
}

// The endpoint paginates at 24 server-side and nothing on screen would show a
// truncated list, so ask for a page large enough to hold any realistic record.
// Records above this are handled by the `next` check in the consumer.
export const MATCH_PAGE_SIZE = 200;

const fetch = (authHeader: string, monitoringId: number | string) => () => {
  const url = new URL(`${env.verifyServiceUrl}individual/screening_match/`);
  url.searchParams.set("screening_record__monitoring", String(monitoringId));
  url.searchParams.set("page_size", String(MATCH_PAGE_SIZE));

  return axios.get<PagedResponse<MonitoringScreeningMatch[]>>(url.href, {
    headers: { Authorization: authHeader },
  });
};

export const monitoringScreeningMatchesQueryKey = (id: number | string) => [
  "monitoringScreeningMatches",
  id,
];

export const useMonitoringScreeningMatches = (
  monitoringId?: number | string,
) => {
  const { authHeader, hasPermission } = useAuth();
  return useQuery(
    monitoringScreeningMatchesQueryKey(monitoringId),
    fetch(authHeader, monitoringId),
    {
      retry: false,
      // the endpoint 403s without screening.view, which logs the user out
      enabled: !!monitoringId && hasPermission("screening.view"),
    },
  );
};
